import { useCallback, useMemo } from 'react';
import { useAchievementTypes, useDispatch, useReduxState } from 'hooks';
import api from 'api';
import {
  AchievementCompletionEditSelf,
  AchievementsIndividualEdit,
  AchievementsIndividualViewInfoModal
} from 'components/achievements';
import { FlexContainer } from 'components/common';
import {
  AccessRequests,
  AccessRequestsAction
} from 'components/common/accessRequest';
import { BoxTableCell } from 'components/common/box';
import { MyTeamPendingRequestsViewAchievement } from '.';

import type { GetTeamPendingRequestsRes } from 'shared-types/responses';
import type { AccessRequestObj } from 'components/common/accessRequest/AccessRequests';

type AvailableProps = {
  requestId: number;
  user: GetTeamPendingRequestsRes['requests'][number]['user'];
  userData: GetTeamPendingRequestsRes['requests'][number]['data'];
  update: () => Promise<void>;
};

const MyTeamPendingRequests = () => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { supervisor },
    user: reqUser
  } = useReduxState();

  const achievementTypes = useAchievementTypes();

  const handleSendRequest = useCallback(
    async ({
      action,
      id,
      message,
      user,
      userData,
      update
    }: AccessRequestObj & AvailableProps) => {
      try {
        const { productId, type } =
          userData.type === 'achievement'
            ? { productId: userData.achievement.id, type: 'achievement' }
            : {
                productId: userData.individualAchievement.id,
                type: 'individual-achievement'
              };

        await api.put(
          `/admin/supervisors/${reqUser.id}/team-pending-requests/${id}`,
          {
            action,
            adminMessage: message,
            productId,
            type,
            userId: user.id
          }
        );
        await update();

        dispatch.updatePageState({
          supervisor: {
            ...supervisor,
            pendingAccessRequests: Math.max(
              supervisor.pendingAccessRequests - 1,
              0
            )
          }
        });
      } catch (err) {
        await dispatch.handleErrorDispatch(err);
      }
    },
    [dispatch, reqUser?.id, supervisor]
  );

  const Header = useMemo(
    () => () =>
      (
        <>
          <BoxTableCell>{messages.commonTitle}</BoxTableCell>
          <BoxTableCell>{messages.commonType}</BoxTableCell>
          <BoxTableCell shrink={true} />
        </>
      ),
    [messages]
  );

  const Body = useMemo(
    () =>
      ({ user, userData }: Pick<AvailableProps, 'user' | 'userData'>) => {
        let title = '';
        let type = '';
        if (userData.type === 'achievement') {
          title = userData.achievement.title;
          type = achievementTypes[userData.achievement.type].title;
        } else {
          title = userData.individualAchievement.title;
          type = messages.individualAchievement;
        }

        return (
          <>
            <BoxTableCell>
              <div style={{ minWidth: 100 }}>{title}</div>
            </BoxTableCell>
            <BoxTableCell>{userData.type}</BoxTableCell>
            <BoxTableCell shrink={true}>
              {userData.type === 'achievement' ? (
                <MyTeamPendingRequestsViewAchievement
                  achievement={userData.achievement}
                  data={userData.requestData}
                  user={user}
                />
              ) : (
                <AchievementsIndividualViewInfoModal
                  individualAchievement={{
                    ...userData.requestData,
                    title: userData.individualAchievement.title
                  }}
                  user={user}
                />
              )}
            </BoxTableCell>
          </>
        );
      },
    [messages]
  );

  const Actions = useMemo(
    () => (props: AvailableProps) =>
      (
        <FlexContainer style={{ flexDirection: 'column' }}>
          {props.userData.type === 'achievement' ? (
            <AchievementCompletionEditSelf
              achievement={props.userData.achievement}
              inDropdown={true}
              initialValues={{
                id: props.requestId,
                ...props.userData.requestData
              }}
              updateAchievements={props.update}
            />
          ) : (
            <AchievementsIndividualEdit
              individualAchievement={{
                ...props.userData.requestData,
                id: props.userData.individualAchievement.id,
                title: props.userData.individualAchievement.title
              }}
              isRequest={true}
              updateData={props.update}
            />
          )}
          <AccessRequestsAction
            action="accept"
            id={props.requestId}
            isBatchAction={false}
            isDropdown={true}
            onSubmit={(payload) => handleSendRequest({ ...payload, ...props })}
          />
          <AccessRequestsAction
            action="deny"
            id={props.requestId}
            isBatchAction={false}
            isDropdown={true}
            onSubmit={(payload) => handleSendRequest({ ...payload, ...props })}
          />
        </FlexContainer>
      ),
    [messages]
  );

  return (
    <AccessRequests<GetTeamPendingRequestsRes>
      customTableElements={{ Header, Body, Actions }}
      hideBatchActions={true}
      hideDefaultUserActions={true}
      title={messages.commonPendingRequests}
      url={`/admin/supervisors/${reqUser.id}/team-pending-requests`}
    />
  );
};

export default MyTeamPendingRequests;
